/**
 * Выбор позиции детали на листе — по списку свободных прямоугольников, с учётом
 * допустимых ориентаций. Три placement-эвристики (`PlacementHeuristic`) для
 * multi-pass (§5.1, CUT-4.2): BSSF / BAF / BLSF.
 *
 * Зазор реза (kerf) НЕ участвует в проверке влезания — деталь ровно в полезную
 * зону должна помещаться. Kerf резервируется позже, при гильотинном split
 * (`optimize.ts`): остаток справа/снизу начинается за вычетом kerf, чтобы между
 * соседними деталями был пропил.
 *
 * @see TRD §5.1.1
 */

import type { FreeRect, Orientation } from './types';

/**
 * Placement-эвристика выбора свободного прямоугольника (меньше score → лучше):
 * - `BSSF` (Best Short Side Fit): score = min(leftoverW, leftoverH).
 * - `BAF`  (Best Area Fit):       score = leftoverW × leftoverH.
 * - `BLSF` (Best Long Side Fit):  score = max(leftoverW, leftoverH).
 */
export type PlacementHeuristic = 'BSSF' | 'BAF' | 'BLSF';

/** Все эвристики в фиксированном (детерминированном) порядке для multi-pass. */
export const PLACEMENT_HEURISTICS: readonly PlacementHeuristic[] = ['BSSF', 'BAF', 'BLSF'];

/**
 * Пороги и величины бонуса крупных остатков (§5.1.8, prefer-long/wide, CUT-4.4c).
 * Контрактных полей под пороги нет → фиксированы здесь по спецификации §5.1.8.
 * Бонус отрицательный (меньше score → лучше): движок жертвует 1–3% КИМ ради
 * 1–2 крупных пригодных остатков.
 */
/** Остаток считается «длинным» (пригодным), если его новая ДЛИНА (remW, ось x) ≥ этого, мм. */
export const PREFER_LONG_MIN_MM = 600;
/** Остаток считается «широким» (пригодным), если его новая ШИРИНА (remH, ось y) ≥ этого, мм. */
export const PREFER_WIDE_MIN_MM = 400;
/** Бонус к score за «длинный» остаток (remW ≥ PREFER_LONG_MIN_MM). */
export const PREFER_LONG_BONUS = -50;
/** Бонус к score за «широкий» остаток (remH ≥ PREFER_WIDE_MIN_MM). */
export const PREFER_WIDE_BONUS = -30;

/** Скоринг остатка по выбранной эвристике (меньше — лучше). */
function rectScore(heuristic: PlacementHeuristic, leftoverW: number, leftoverH: number): number {
  switch (heuristic) {
    case 'BAF':
      return leftoverW * leftoverH;
    case 'BLSF':
      return Math.max(leftoverW, leftoverH);
    case 'BSSF':
    default:
      return Math.min(leftoverW, leftoverH);
  }
}

/** Выбранная позиция: свободный прямоугольник + ориентация + фактические габариты детали. */
export interface PlacementChoice {
  /** Индекс выбранного freeRect в исходном списке. */
  rectIndex: number;
  rect: FreeRect;
  rotated: boolean;
  /** Фактический габарит детали в выбранной ориентации: вдоль длины (x). */
  partW: number;
  /** Фактический габарит детали в выбранной ориентации: вдоль ширины (y). */
  partH: number;
  /** Score выбранной позиции по эвристике (меньше — плотнее). Для глобального best-fit. */
  score: number;
}

/**
 * Поиск лучшей позиции для детали `length`×`width` среди `freeRects` по выбранной
 * `heuristic` (по умолчанию BSSF — поведение CUT-4.1).
 *
 * `preferLong`/`preferWide` (§5.1.8, CUT-4.4c) добавляют бонус к score за крупный
 * пригодный остаток: `preferLong` → −50 при remW (leftoverW, новая длина) ≥ 600;
 * `preferWide` → −30 при remH (leftoverH, новая ширина) ≥ 400. Дефолт `false` —
 * чистая эвристика (back-compat CUT-4.2/4.4b).
 *
 * Детерминирован: при равном score выигрывает меньший индекс freeRect, затем
 * ориентация `normal` (первая в списке).
 *
 * Возвращает `null`, если деталь не влезает ни в один freeRect ни в одной
 * допустимой ориентации.
 */
export function findBestRect(
  freeRects: readonly FreeRect[],
  length: number,
  width: number,
  orientations: readonly Orientation[],
  heuristic: PlacementHeuristic = 'BSSF',
  preferLong = false,
  preferWide = false,
): PlacementChoice | null {
  let best: PlacementChoice | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let i = 0; i < freeRects.length; i++) {
    const fr = freeRects[i];
    if (fr === undefined) continue;

    for (const orient of orientations) {
      const partW = orient === 'normal' ? length : width;
      const partH = orient === 'normal' ? width : length;

      const leftoverW = fr.w - partW; // remW: новая ДЛИНА остатка (ось x)
      const leftoverH = fr.h - partH; // remH: новая ШИРИНА остатка (ось y)
      if (leftoverW < 0 || leftoverH < 0) continue; // не влезает

      // §5.1.8: бонус крупных остатков (меньше score → лучше). Запекается в
      // choice.score → корректно пробрасывается в cross-layout best-fit (optimize.ts).
      const bonus =
        (preferLong && leftoverW >= PREFER_LONG_MIN_MM ? PREFER_LONG_BONUS : 0) +
        (preferWide && leftoverH >= PREFER_WIDE_MIN_MM ? PREFER_WIDE_BONUS : 0);
      const score = rectScore(heuristic, leftoverW, leftoverH) + bonus;
      if (score < bestScore - 1e-9) {
        bestScore = score;
        best = { rectIndex: i, rect: fr, rotated: orient === 'rotated', partW, partH, score };
      }
    }
  }

  return best;
}
